import { useMemo, useState } from "react";
import { CommandItem } from "@volca/ui/components/ui/command";
import { toast } from "@volca/ui/lib/toast";
import { getProductCapabilities } from "@/business/product-capabilities";
import { useGetBusiness } from "@/business/query";
import { useBusiness } from "@/core/business";
import { FEATURE_FLAG_NAMES } from "@/core/feature-flags/feature-flag-name";
import { useFeatureFlag } from "@/core/feature-flags/use-feature-flag";
import {
  ownersWriteTemplates,
  TEMPLATES_BY_VOLCA,
} from "@/profitability/access";
import { REPORT_PERIOD_PRESETS } from "@/profitability/constants";
import { useCreateReport, useReportHistory } from "@/profitability/query";
import { useReports } from "@/profitability/report-query";
import {
  PROFITABILITY_NEW_TEMPLATE_PATH,
  canManageProfitability,
  LEGACY_REPORTS_PATH,
  profitabilityTemplatePath,
  profitabilityRunPath,
} from "@/profitability/routes";
import type { ReportDates } from "@/profitability/types";
import { dateRangeError, reportError, toIsoDate } from "@/profitability/utils";
import { useGetMyUser } from "@/user/query";
import { CommandLoadMore, CommandNestedState } from "./nested-list";

const presetDates = (
  preset: (typeof REPORT_PERIOD_PRESETS)[number],
): ReportDates | null => {
  const range = preset.getValue();
  if (!range.from || !range.to) return null;
  return { start_date: toIsoDate(range.from), end_date: toIsoDate(range.to) };
};

export const ProfitabilityPage = ({
  onNavigate,
}: {
  onNavigate: (to: string) => void;
}) => {
  const { data: user, isLoading: isUserLoading } = useGetMyUser();
  const { businessId, isLoading: isBusinessesLoading } = useBusiness();
  const business = useGetBusiness(businessId);
  const { enabled: documentsOnly, isLoading: isFlagLoading } = useFeatureFlag(
    FEATURE_FLAG_NAMES.profitability_documents_only,
  );
  const isLoading =
    isBusinessesLoading || isUserLoading || business.isLoading || isFlagLoading;
  const capabilities = getProductCapabilities({
    user,
    business: business.data,
    settings: business.data?.enabled_products,
    documentsOnly,
  });

  if (isLoading) {
    return (
      <CommandNestedState
        heading="Reports"
        loading
        error={null}
        empty="No reports."
        count={0}
      />
    );
  }

  if (business.isError) {
    return (
      <CommandNestedState
        heading="Reports"
        loading={false}
        error={`Couldn't load business details. ${reportError(business.error)}`}
        onRetry={() => void business.refetch()}
        empty="No reports."
        count={0}
      />
    );
  }

  if (!capabilities.profitability.canOpenReports) {
    return (
      <CommandNestedState
        heading="Reports"
        loading={false}
        error={null}
        empty="Reports aren't available."
        count={0}
      />
    );
  }

  const current = documentsOnly;
  return !current ? (
    <ProfitabilityReports businessId={businessId} onNavigate={onNavigate} />
  ) : (
    <TemplateCommands
      businessId={businessId}
      onNavigate={onNavigate}
      canManage={canManageProfitability(user)}
      canAuthor={ownersWriteTemplates(business.data)}
      hasLegacy={!!business.data?.profitability_workflows?.includes(1)}
    />
  );
};

const ProfitabilityReports = ({
  businessId,
  onNavigate,
}: {
  businessId: string | null;
  onNavigate: (to: string) => void;
}) => {
  const history = useReportHistory(businessId, undefined, 1);
  const create = useCreateReport(businessId, 1);
  const [pending, setPending] = useState<string | null>(null);
  const runs = useMemo(
    () => history.data?.pages.flatMap((page) => page.items) ?? [],
    [history.data],
  );

  return (
    <>
      <CommandNestedState
        heading="New report"
        loading={false}
        error={null}
        empty="No report periods."
        count={REPORT_PERIOD_PRESETS.length}
      >
        {REPORT_PERIOD_PRESETS.map((preset) => (
          <CommandItem
            key={preset.label}
            value={`new report ${preset.label}`}
            disabled={create.isPending}
            onSelect={() => {
              const fail = (description: string) =>
                toast.error("The report could not be started", { description });
              const dates = presetDates(preset);
              if (!dates) {
                fail("Enter a start and end date.");
                return;
              }
              const invalid = dateRangeError(dates);
              if (invalid) {
                fail(invalid);
                return;
              }
              setPending(preset.label);
              // An empty list, not none: a missing one makes the API carry
              // forward whatever documents the last run of these dates read.
              create.mutate(
                { ...dates, document_ids: [] },
                {
                  onSuccess: (run) => onNavigate(profitabilityRunPath(run.id)),
                  onError: (error) => fail(reportError(error)),
                  onSettled: () => setPending(null),
                },
              );
            }}
          >
            <span>
              {pending === preset.label ? "Starting report" : preset.label}
            </span>
          </CommandItem>
        ))}
      </CommandNestedState>
      <CommandNestedState
        heading="Reports"
        loading={history.isLoading}
        error={
          history.isError && !history.data
            ? `Couldn't load reports. ${reportError(history.error)}`
            : null
        }
        onRetry={() => void history.refetch()}
        empty="No reports yet."
        count={runs.length}
      >
        {runs.map((run) => (
          <CommandItem
            key={run.id}
            value={`${run.display.period} ${run.start_date} ${run.end_date} ${run.id}`}
            onSelect={() => onNavigate(profitabilityRunPath(run.id))}
          >
            <span>{run.display.period}</span>
          </CommandItem>
        ))}
        {history.hasNextPage && (
          <CommandLoadMore
            loading={history.isFetchingNextPage}
            onLoad={() => void history.fetchNextPage()}
          />
        )}
      </CommandNestedState>
    </>
  );
};

const TemplateCommands = ({
  businessId,
  onNavigate,
  canManage,
  canAuthor,
  hasLegacy,
}: {
  businessId: string | null;
  onNavigate: (to: string) => void;
  canManage: boolean;
  canAuthor: boolean;
  hasLegacy: boolean;
}) => {
  const reports = useReports(businessId);
  return (
    <>
      {canManage && canAuthor && (
        <CommandItem
          value="new agent template"
          onSelect={() => onNavigate(PROFITABILITY_NEW_TEMPLATE_PATH)}
        >
          New agent
        </CommandItem>
      )}
      {canManage && hasLegacy && (
        <CommandItem
          value="servicetitan report history"
          onSelect={() => onNavigate(LEGACY_REPORTS_PATH)}
        >
          ServiceTitan report history
        </CommandItem>
      )}
      <CommandNestedState
        heading="Agents"
        loading={reports.isPending}
        error={reports.isError ? "Agents could not load. Try again." : null}
        onRetry={() => void reports.refetch()}
        empty={
          !canManage
            ? "No agents have been shared with you."
            : canAuthor
              ? "No agents yet. Select New agent to create one."
              : `No agents yet. ${TEMPLATES_BY_VOLCA}`
        }
        count={reports.data?.length ?? 0}
      >
        {reports.data?.map((report) => (
          <CommandItem
            key={report.id}
            value={`${report.name} ${report.description ?? ""} ${report.id}`}
            onSelect={() => onNavigate(profitabilityTemplatePath(report.id))}
          >
            {report.name}
          </CommandItem>
        ))}
      </CommandNestedState>
    </>
  );
};
